// frontend/src/pages/Home.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";

// Helper function to add language suffix to image URLs
// Example: /images/categories/123.jpg with lang 'de' becomes /images/categories/123_de.jpg
const getLocalizedImageUrl = (url, language) => {
  if (!url || !language) return url;

  // Insert language suffix before file extension
  const lastDotIndex = url.lastIndexOf(".");
  if (lastDotIndex === -1) return url; // No extension found

  return url.slice(0, lastDotIndex) + "_" + language + url.slice(lastDotIndex);
};

const CategoryCard = ({ category }) => {
  const { t, language } = useLanguage();
  const { hasAccess } = useAuth();
  const canAccess = hasAccess(category.contract);

  // Special handling for Articles category
  const linkTo = category.slug === 'articles' ? '/articles' : `/category/${category.slug}`;

  return (
    <Link
      to={linkTo}
      className={`group relative bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 border border-gray-200 ${
        canAccess ? "hover:scale-105 hover:shadow-lg" : "opacity-60"
      } cursor-pointer`}
    >
      <div className="aspect-video bg-gradient-to-br from-[#00A3E0] to-[#002855] relative">
        <img
          // Try language-specific image first, e.g., image_de.jpg for German
          src={getLocalizedImageUrl(category.imageUrl, language)}
          alt={t(category.title)}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback chain for category images:
            const currentSrc = e.target.src;
            const hasLangSuffix = currentSrc.includes("_" + language + ".");

            if (hasLangSuffix) {
              // Step 1: Remove language suffix and try generic image
              const genericUrl = currentSrc.replace("_" + language + ".", ".");
              e.target.src = genericUrl;
            } else {
              // Step 2: Hide image if generic version also fails
              e.target.style.display = "none";
            }
          }}
        />

        {!canAccess && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        )}

        {category.contract !== "Free" && (
          <div className="absolute top-2 right-2">
            <ContractBadge contract={category.contract} />
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-[#002855] mb-1">
          {t(category.title)}
        </h3>
      </div>
    </Link>
  );
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    loadCategories();
  }, [language]);

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      if (response.success) {
        setCategories(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-[#DA291C]">Error: {error}</div>
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#002855] mb-2">
          {t({
            en: "Understand more. Fly better.",
            de: "Mehr verstehen. Besser fliegen.",
          })}
        </h1>
        <p className="text-lg text-gray-600">
          {t({
            en: "Videos, playlists, and more to help you master paragliding.",
            de: "Videos, Playlists und mehr, um das Gleitschirmfliegen zu meistern.",
          })}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <CategoryCard key={category.category_id} category={category} />
        ))}
      </div>
    </main>
  );
};

export default Home;
